import {
  AppRegion,
  getProviderMethod,
  isValidProviderMethod,
  PaymentMethod,
  PaymentProvider,
  ProviderMethodSettings,
  ValidProviderMethod,
  PaymentSettings,
  ScenarioSettings,
  PaymentRowsSettings,
  isPaymentMethod,
  getPrePayProviderName,
  PaymentMethodDisableReason,
  PaymentMethodAvailability,
  getMethodMinTotal,
} from '@ca/common'
import { DealType, OrderScenario, Store } from '@ca/common/types'
import { useStore } from 'hooks/useSession'
import { useMemo } from 'react'
import { useDealsById } from './useDeal'
import { SavedOrder } from './state/orderState'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import { getAppRegion, useAppRegion } from 'storage/useRemoteConfigParams'
import { getScenario } from './useScenario'
import { getResultTotal } from './useOrderTotal'

type PaymentSetting = {
  allowed: PaymentMethod[]
  default: PaymentMethod
  merged?: boolean
}

type PaymentType = 'merged' | OrderScenario

const GLOBAL_ALLOWED: PaymentMethod[] = ['monthly']

const PaymentScenarioSettings: Record<PaymentType, PaymentSetting> = {
  'base.normal': {
    default: 'terminal',
    allowed: ['terminal', 'invoice', 'paylink'],
  },
  'base.tgbo': {
    default: 'monthly',
    allowed: ['monthly', 'terminal'],
  },
  'extra.tgbo': {
    default: 'monthly',
    allowed: ['monthly', 'terminal'],
  },
  'extra.normal': {
    default: 'terminal',
    allowed: ['terminal', 'invoice', 'paylink'],
  },
  'base.simplified': {
    default: 'paylink',
    allowed: ['paylink'],
  },
  'extra.simplified': {
    default: 'paylink',
    allowed: ['paylink'],
  },
  merged: {
    default: 'terminal',
    allowed: ['terminal'],
    merged: true,
  },
}

export type PaymentItems = {
  type: DealType
  isExtra: boolean
  flowNames: Array<string | undefined>
  price?: number
}

const getPaymentType = (items: PaymentItems[]): PaymentType | undefined => {
  // For payments if we are mixing flows we want to force normal payment settings
  const flowNames = items.map((item) => item.flowNames).flat()
  const singleFlow = flowNames.every((name) => name === flowNames[0])
  const flowName = singleFlow ? flowNames[0] || 'normal' : 'normal'

  if (items.length > 1) {
    const scenarios = items.map((item) =>
      getScenario(item.isExtra ? 'extra' : 'base', item.type, flowName)
    )
    const isTerminal = scenarios.every((scenario) =>
      PaymentScenarioSettings[scenario].allowed.includes(PaymentMethod.terminal)
    )
    if (isTerminal) {
      return 'merged'
    }
    // If not all support terminal then we don't have merged payment
    return undefined
  }

  if (items.length === 0) {
    return undefined
  }

  const item = items[0]
  const scenario = getScenario(
    item.isExtra ? 'extra' : 'base',
    item.type,
    flowName
  )

  return scenario
}

const getPaymentSettings = (items: PaymentItems[]) => {
  const type = getPaymentType(items)
  if (!type) {
    return undefined
  }
  return PaymentScenarioSettings[type]
}

const isMethodSupported = ({
  region,
  method,
  storeMethods,
}: {
  method: PaymentMethod
  region: AppRegion
  storeMethods?: Store['prepay_method']
}) => {
  const isSupported = region.payments[method].length > 0

  if (!isSupported) {
    return false
  }

  // We want to check if we have at lest one valid provider
  const providers = region.payments[method].filter((p) => {
    // Check if we support this provider for this store
    const name = getPrePayProviderName(p)
    if (name) {
      return storeMethods?.some((m) => m.key === name)
    }
    // No other limitations, we allow it
    return true
  })

  return providers.length > 0
}

export const useStorePaymentMethods = () => {
  const store = useStore()

  const region = useAppRegion()

  const methods = useMemo(() => {
    const methods = new Set<PaymentMethod>()

    const addSupportedMethod = (method: string) => {
      if (!isPaymentMethod(method)) {
        return
      }
      if (
        !isMethodSupported({
          method,
          region,
          storeMethods: store?.prepay_method,
        })
      ) {
        return
      }
      methods.add(method)
    }

    GLOBAL_ALLOWED.forEach((m) => {
      addSupportedMethod(m)
    })

    if (!region.validatePrepayMethods) {
      Object.values(PaymentMethod).forEach((m) => {
        addSupportedMethod(m)
      })
      return Array.from(methods).reverse()
    }

    if (store?.prepay_method) {
      store.prepay_method.forEach((m) => {
        addSupportedMethod(m.key)
      })
    }

    return Array.from(methods).reverse()
  }, [store, region])

  return {
    methods,
    data: store,
  }
}

const isBatchMethod = (method: PaymentMethod) => {
  const provider = resolveProvider(method)
  if (!provider) return false
  const key = getProviderMethod(provider, method)
  if (!isValidProviderMethod(key)) return false
  return ProviderMethodSettings[key].batch === true
}

const getBatchMethods = (items: PaymentItems[]): PaymentMethod[] => {
  if (items.length === 0) return []
  const allItemSettings = items.map((item) => getPaymentSettings([item]))
  const firstItemMethods = allItemSettings[0]?.allowed ?? []
  return firstItemMethods.filter(
    (method) =>
      isBatchMethod(method) &&
      allItemSettings.every((s) => s?.allowed.includes(method) ?? false)
  )
}

const getItemsTotal = (items: PaymentItems[]) => {
  return items.reduce((sum, item) => sum + (item.price ?? 0), 0)
}

const getDisableReasons = ({
  method,
  storeMethods,
  isMerged,
  isMergedStoreExcluded,
  isBatch,
  total,
}: {
  method: PaymentMethod
  storeMethods: PaymentMethod[]
  isMerged: boolean
  isMergedStoreExcluded: boolean
  isBatch: boolean
  total: number
}): PaymentMethodDisableReason[] => {
  const reasons: PaymentMethodDisableReason[] = []
  if (!storeMethods.includes(method)) reasons.push('disabled')
  if (
    isMerged &&
    !isBatch &&
    (isMergedStoreExcluded ||
      !PaymentScenarioSettings.merged.allowed.includes(method))
  ) {
    reasons.push('merged')
  }
  const minTotal = getMethodMinTotal(method)
  if (minTotal > 0 && total < minTotal) reasons.push('min_value')
  return reasons
}

const useIsMergedStoreExcluded = () => {
  const merged = useConfigConditionValue('payments:merged', 'stores')
  const store = useStorePaymentMethods()
  return useMemo(() => {
    if (!merged?.store_refs) return false
    if (!store.data?.ref) return true
    return !merged.store_refs.includes(store.data.ref)
  }, [merged, store.data])
}

export const usePaymentItemsMethods = (items: PaymentItems[]) => {
  const settings = getPaymentSettings(items)
  const store = useStorePaymentMethods()
  const isMergedStoreExcluded = useIsMergedStoreExcluded()

  const total = useMemo(() => getItemsTotal(items), [items])

  const availability = useMemo((): PaymentMethodAvailability[] => {
    if (!settings) return []

    let allMethods = settings.allowed

    // If merged, also include batch methods supported by ALL items
    const batchMethods = settings.merged ? getBatchMethods(items) : []
    if (batchMethods.length > 0) {
      allMethods = [...new Set([...allMethods, ...batchMethods])]
    }

    // If it's a merged payment and all extra items have price 0, inherit methods from base item
    if (settings.merged && items.length > 1) {
      const itemsBase = items.filter((item) => !item.isExtra)
      const itemsExtra = items.filter((item) => item.isExtra)
      const isAllBasePrice = itemsBase.every((item) => (item.price ?? 0) > 0)
      const isAllExtraFree = itemsExtra.every((item) => (item.price ?? 0) === 0)
      if (isAllBasePrice && isAllExtraFree) {
        const allowedBaseMethods = getPaymentSettings(itemsBase)?.allowed ?? []
        allMethods = Array.from(
          new Set([...settings.allowed, ...allowedBaseMethods])
        )
      }
    }

    return allMethods.map((method) => {
      const disableReasons = getDisableReasons({
        method,
        storeMethods: store.methods,
        isMerged: !!settings.merged,
        isMergedStoreExcluded: !!settings.merged && isMergedStoreExcluded,
        isBatch: batchMethods.includes(method),
        total,
      })
      return { method, available: disableReasons.length === 0, disableReasons }
    })
  }, [settings, store.methods, items, total, isMergedStoreExcluded])

  const methods = useMemo(
    () => availability.filter((a) => a.available).map((a) => a.method),
    [availability]
  )

  if (!settings) {
    return {
      merged: false,
      methods,
      availability,
      defaultMethod: PaymentMethod.terminal,
    }
  }

  return {
    merged: settings.merged,
    methods,
    availability,
    defaultMethod: methods.includes(settings.default)
      ? settings.default
      : methods[0],
  }
}

export const usePaymentItems = (items: SavedOrder[]) => {
  const deals = useDealsById()

  const res = useMemo(() => {
    return items.map((order) => {
      const dealId = order.deals.base?.dealId
      const deal = dealId ? deals[dealId] : undefined

      return {
        type: deal?.type ?? 'normal',
        isExtra: !!order.primaryId,
        price: getResultTotal(order.result) ?? 0,
        flowNames: order.order.items.map((item) => item.flowName),
      } satisfies PaymentItems
    })
  }, [items, deals])

  return res
}

// export const usePaymentMethods = () => {
//   const deal = useActiveDeal()
//   const isExtra = useIsExtraDeal()
//   const flowName = useFlowName()
//   return usePaymentItemsMethods([
//     {
//       type: deal?.type ?? 'normal',
//       isExtra,
//       flowName,
//     },
//   ])
// }

type InferredMethodSettings<T extends PaymentMethod | undefined> =
  T extends PaymentMethod
    ? (typeof ProviderMethodSettings)[ValidProviderMethod]
    : undefined

export const resolveProvider = (
  method: PaymentMethod,
  customProvider?: PaymentProvider,
  region?: AppRegion
) => {
  region = region ?? getAppRegion()
  if (customProvider) {
    const provider = region.payments[method].find((p) => p === customProvider)
    if (provider) {
      return provider
    }
    console.warn(`Provider ${customProvider} not found for method ${method}`)
  }
  const defaultProvider = region.payments[method][0]
  if (!defaultProvider) {
    console.warn(`Provider not found for method ${method}`)
    return undefined
  }
  return defaultProvider
}

const resolveCustom = (
  base: PaymentSettings,
  partial: Partial<ScenarioSettings>[]
) => {
  if (base.custom !== 'enabled') {
    // Base payment method does not allow custom amount
    return base.custom
  }

  const isCustomEnabled = partial.every((p) => p.custom === 'enabled')
  if (isCustomEnabled) {
    return 'enabled'
  }
  const isCustomDisabled = partial.every((p) => p.custom === 'disabled')
  if (isCustomDisabled) {
    return 'disabled'
  }
  return 'hidden'
}

const resolveRows = (
  base: PaymentSettings,
  partial: Partial<ScenarioSettings>[]
) => {
  const rows: PaymentRowsSettings = base.rows ? { ...base.rows } : {}
  partial.forEach((p) => {
    const keys = Object.keys(p.rows ?? {}) as (keyof PaymentRowsSettings)[]
    keys.forEach((key) => {
      if (rows[key] === false) {
        return
      }
      rows[key] = p.rows?.[key]
    })
  })

  return rows satisfies PaymentRowsSettings
}

const combinePaymentSettings = (
  base: PaymentSettings,
  partial: Partial<ScenarioSettings>[]
) => {
  // Only keep custom if all partial settings have custom
  const custom = resolveCustom(base, partial)

  const resolved = {
    ...base,
    custom,
    rows: resolveRows(base, partial),
  } satisfies PaymentSettings

  return resolved
}

export function getMethodSettings<T extends PaymentMethod | undefined>(
  method: T,
  customProvider?: PaymentProvider,
  scenarios?: OrderScenario[]
): InferredMethodSettings<T> {
  if (!method) {
    return undefined as InferredMethodSettings<T>
  }
  let provider = resolveProvider(method, customProvider)
  if (!provider) {
    throw new Error(`Provider not found for method ${method}`)
  }
  let key = getProviderMethod(provider, method)

  if (!isValidProviderMethod(key)) {
    // Fallback to default provider
    console.warn(`Invalid provider method ${key} for method ${method}`)
    provider = resolveProvider(method)
    if (!provider) {
      throw new Error(`Provider not found for method ${method}`)
    }
    key = getProviderMethod(provider, method)
  }
  if (!isValidProviderMethod(key)) {
    // This means we have invalid config
    throw new Error(`Invalid provider method ${key} for method ${method}`)
  }

  const settings = ProviderMethodSettings[key] as InferredMethodSettings<T>
  if (!settings) {
    console.warn(
      `Settings not found for provider method ${key} for method ${method}`
    )
    return undefined as InferredMethodSettings<T>
  }
  if (!scenarios || scenarios.length === 0) {
    return settings
  }
  const scenarioSettings = scenarios
    .map((scenario) => settings?.scenarios?.[scenario])
    .filter((v) => !!v)
  if (scenarioSettings.length === 0) {
    return settings
  }
  const res = combinePaymentSettings(
    settings,
    scenarioSettings
  ) as InferredMethodSettings<T>

  return res
}

export function usePaymentMethodSettings<T extends PaymentMethod | undefined>(
  method: T,
  options?: {
    scenarios?: OrderScenario[]
    provider?: PaymentProvider
  }
): InferredMethodSettings<T> {
  const { scenarios, provider: customProvider } = options ?? {}
  return useMemo(() => {
    return getMethodSettings(method, customProvider, scenarios)
  }, [method, customProvider, scenarios])
}

export function getDefaultProvider(
  method: PaymentMethod,
  region?: AppRegion
): PaymentProvider {
  region = region ?? getAppRegion()
  return region.payments[method][0]
}

export const useDefaultProvider = (method: PaymentMethod) => {
  const region = useAppRegion()
  return useMemo(() => {
    return getDefaultProvider(method, region)
  }, [method, region])
}

const uniq = <T>(array: T[]): T[] => {
  return [...new Set(array)]
}

export const useOrdersMethods = (orders: SavedOrder[]) => {
  const items = usePaymentItems(orders)
  const total = useMemo(() => getItemsTotal(items), [items])

  const settings = useMemo(() => {
    const allSettings = getPaymentSettings(items)
    const single = items.map((item) => getPaymentSettings([item]))

    const singleMethods = single.map((s) => s?.allowed ?? []).flat()
    const allMethods = [...(allSettings?.allowed ?? []), ...singleMethods]

    const merged = !!allSettings?.merged

    const combined = {
      allowed: uniq(allMethods),
      default:
        allSettings?.default ?? single[0]?.default ?? PaymentMethod.terminal,
      merged,
    } satisfies PaymentSetting
    return combined
  }, [items])

  const store = useStorePaymentMethods()
  const isMergedStoreExcluded = useIsMergedStoreExcluded()

  const batchMethods = useMemo(
    () => (settings.merged ? getBatchMethods(items) : []),
    [settings.merged, items]
  )

  const availability = useMemo((): PaymentMethodAvailability[] => {
    return settings.allowed.map((method) => {
      const disableReasons = getDisableReasons({
        method,
        storeMethods: store.methods,
        isMerged: settings.merged,
        isMergedStoreExcluded,
        isBatch: batchMethods.includes(method),
        total,
      })
      return { method, available: disableReasons.length === 0, disableReasons }
    })
  }, [
    settings.allowed,
    settings.merged,
    store.methods,
    total,
    isMergedStoreExcluded,
    batchMethods,
  ])

  const methods = useMemo(
    () => availability.filter((a) => a.available).map((a) => a.method),
    [availability]
  )

  return {
    merged: settings.merged,
    allowed: methods,
    availability,
    default: methods.includes(settings.default) ? settings.default : methods[0],
  }
}
